import { useEffect, useState } from "react";
import {
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  UploadCloud,
  Banknote,
} from "lucide-react";
import { useAdminRecordApplicationBankPaymentMutation } from "../api/upgradeApi";
import { useGetLedgerAccountsQuery } from "../../billing/api/billingApi";
import { describeApiError } from "../../../lib/apiError";

const naira = (n: number | null | undefined) =>
  n == null
    ? "—"
    : "₦" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Record an upgrade application fee that was paid into the bank.
 *
 * Unlike the member-first modal on the list screen, there is nothing to look up
 * here: the application on screen already states the grade, the route and the
 * fee, so the form is only about the money — how much arrived, into which
 * account, on what date, and the teller's slip as evidence.
 */
export default function RecordApplicationBankPaymentModal({
  open,
  onClose,
  onDone,
  app,
}: {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
  app: any;
}) {
  const feeInfo = app?.application_fee ?? {};
  const requiresRoute: boolean = !!feeInfo.requires_route;
  const routeFees: Record<string, number> | null = feeInfo.route_fees ?? null;

  const [route, setRoute] = useState<string>(app?.route ?? "");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [bankAccountId, setBankAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [msg, setMsg] = useState<string | null>(null);
  const [held, setHeld] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<any[]>([]);

  const [record, { isLoading: saving }] = useAdminRecordApplicationBankPaymentMutation();
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  // The fee this application is priced at: the configured one for its
  // grade/route, else whatever it was already stamped with.
  const chosenRoute = requiresRoute ? route : app?.route ?? null;
  const computedFee: number | null =
    (requiresRoute && chosenRoute ? routeFees?.[chosenRoute] : feeInfo.fee) ??
    (app?.amount != null ? Number(app.amount) / 100 : null);

  // Reset each time the modal opens, and prefill the amount with the fee so the
  // common case — the exact fee paid in — is one press.
  useEffect(() => {
    if (!open) return;
    setRoute(app?.route ?? "");
    setPaymentMethod("bank_transfer");
    setBankAccountId("");
    setReference("");
    setPaidAt("");
    setNote("");
    setFile(null);
    setMsg(null);
    setHeld(false);
    setErr(null);
    setBlockers([]);
  }, [open, app?.route]);

  useEffect(() => {
    if (open && computedFee != null) setAmount(String(computedFee));
  }, [open, computedFee]);

  // A different route carries its own exam/CPD requirements, so any blockers
  // raised against the previous one no longer apply.
  useEffect(() => {
    setBlockers([]);
  }, [route]);

  const submit = async (override = false) => {
    setErr(null);
    setMsg(null);

    if (requiresRoute && !route) return setErr("Select a route (exams or matured).");
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt <= 0) return setErr("Enter a valid amount.");

    const fd = new FormData();
    if (requiresRoute && route) fd.append("route", route);
    fd.append("amount", String(amt));
    fd.append("payment_method", paymentMethod);
    if (bankAccountId) fd.append("bank_account_id", bankAccountId);
    if (reference.trim()) fd.append("reference", reference.trim());
    if (paidAt) fd.append("paid_at", paidAt);
    if (note.trim()) fd.append("note", note.trim());
    if (file) fd.append("proof", file);
    if (override) fd.append("override", "1");

    try {
      const res: any = await record({ id: app.id, body: fd }).unwrap();
      setBlockers([]);
      setHeld(!!res?.held_ineligible);
      setMsg(res?.message || "Bank payment recorded.");
      onDone?.();
    } catch (e: any) {
      // The member fails this route's eligibility checks (exam result, CPD,
      // seniority). Surface them so the admin can consciously override — the
      // money did arrive, whatever the requirements say.
      if (e?.data?.code === "UPGRADE_ELIGIBILITY_BLOCKED" && Array.isArray(e.data.blockers)) {
        setBlockers(e.data.blockers);
        setErr(null);
      } else {
        setBlockers([]);
        setErr(describeApiError(e, "Could not record the payment."));
      }
    }
  };

  if (!open) return null;

  const overpay =
    computedFee != null && Number(amount) > computedFee ? Number(amount) - computedFee : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 md:p-8">
      <div className="w-full max-w-lg bg-white rounded-[28px] shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center justify-center">
              <Banknote className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tight">Record Bank Payment</h2>
              <p className="text-xs font-semibold text-gray-500">
                Application #{String(app?.id ?? "").padStart(6, "0")} · upgrade fee paid offline
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {msg ? (
            <div className="text-center py-6 space-y-4">
              {held ? (
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
              ) : (
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              )}
              <p className="text-sm font-semibold text-gray-700">{msg}</p>
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* What this payment is for — read off the application, not chosen */}
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                <p className="text-sm font-black text-gray-900 truncate">{app?.user?.name ?? "—"}</p>
                <p className="text-xs text-gray-500 truncate">
                  {[app?.user?.member?.membership_no, app?.user?.email].filter(Boolean).join(" · ")}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-bold border border-indigo-100">
                    {app?.from_category?.name ?? "—"} → {app?.to_category?.name ?? "—"}
                  </span>
                  {app?.route && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold border border-gray-200 capitalize">
                      {app.route} route
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-100">
                    Fee {naira(computedFee)}
                  </span>
                </div>
              </div>

              {/* Route — only when the grade needs one and the member never chose it */}
              {requiresRoute && (
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                    Route
                  </label>
                  <select
                    value={route}
                    onChange={(e) => setRoute(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="">— Select route —</option>
                    {Object.keys(routeFees ?? {}).map((r) => (
                      <option key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)} — {naira(routeFees?.[r])}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                  Amount paid (₦)
                </label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Fee: {naira(computedFee)}
                  {overpay > 0 && (
                    <span className="text-amber-600 font-semibold">
                      {" "}· Overpayment {naira(overpay)} → member advance
                    </span>
                  )}
                </p>
              </div>

              {/* Method + receiving account */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                    Method
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="cash">Cash</option>
                    <option value="pos">POS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                    Bank account
                  </label>
                  <select
                    value={bankAccountId}
                    onChange={(e) => setBankAccountId(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="">— Select —</option>
                    {(bankAccounts as any[]).map((acc: any) => (
                      <option key={acc.id} value={acc.id}>
                        {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number]
                          .filter(Boolean)
                          .join(" — ")}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reference + date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                    Bank reference
                  </label>
                  <input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Optional"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                    Payment date
                  </label>
                  <input
                    type="date"
                    value={paidAt}
                    onChange={(e) => setPaidAt(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
                  />
                </div>
              </div>

              {/* Evidence of payment */}
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                  Evidence of payment
                </label>
                <label className="flex items-center gap-3 border border-dashed border-gray-300 rounded-xl px-4 py-3 cursor-pointer hover:bg-gray-50">
                  <UploadCloud className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600 truncate">
                    {file ? file.name : "Upload teller / transfer receipt (jpg, png, pdf)"}
                  </span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                <p className="text-[11px] text-gray-400 mt-1">
                  Stored against the payment and openable from this screen afterwards.
                </p>
              </div>

              {/* Note */}
              <div>
                <label className="block text-xs font-black uppercase tracking-widest text-gray-500 mb-1.5">
                  Note
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder="Optional — anything to record about this payment"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none"
                />
              </div>

              {blockers.length > 0 && (
                <div className="text-sm bg-amber-50 border border-amber-200 rounded-xl px-3 py-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-800 font-bold">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>Member does not meet the requirements for this upgrade</span>
                  </div>
                  <ul className="list-disc pl-6 space-y-1 text-amber-800/90">
                    {blockers.map((b: any, i: number) => (
                      <li key={b?.code ?? i}>
                        <span className="font-semibold">{b?.title ?? "Requirement not met"}</span>
                        {b?.message ? ` — ${b.message}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] font-semibold text-amber-700/80">
                    The payment was made, so you can override and record it anyway — this will be logged
                    against your account, and the application is held as paid until the member qualifies.
                  </p>
                </div>
              )}

              {err && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!msg && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={() => submit(blockers.length > 0)}
              disabled={saving}
              className={`px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 flex items-center gap-2 ${
                blockers.length > 0 ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {blockers.length > 0 ? "Override & record anyway" : "Record payment"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
